import React, { useState, useEffect, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Router, Route, Switch, Link, useLocation } from 'wouter';
import { SupabaseAuthProvider, useSupabaseAuth } from './contexts/SupabaseAuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AIConsentProvider } from './contexts/AIConsentContext';
import { HomeNavigationLogo } from './components/HomeNavigationLogo';
import { SmartLogin } from './components/auth/SmartLogin';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ProtectedPlaybooksRoute } from './components/playbooks/ProtectedPlaybooksRoute';
import { ProtectedAdminRoute } from './components/auth/ProtectedAdminRoute';
import { RecoveryRedirect } from './components/auth/RecoveryRedirect';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Navigation } from './components/Navigation';
import { BetaWelcomeBack } from './components/BetaWelcomeBack';
import { LoadingFallback } from './components/LoadingFallback';
import { getSubdomain, redirectToProfile } from './utils/subdomainRouter';
import { ProductionOnboarding } from './components/onboarding/ProductionOnboarding';
import { useFirstTimeUser } from './hooks/useFirstTimeUser';
import './App.css';

/**
 * Week 9 Performance Optimization - Phase 1: Route-Based Code Splitting
 * All pages are now lazy-loaded to reduce initial bundle size from 4 MB to ~1 MB
 */

// Auth pages (small, keep eager for faster login)
import LoginComponent from './components/auth/LoginComponent';
import { AuthCallback } from './pages/AuthCallback';
import { EmailVerification } from './pages/EmailVerification';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { PasswordResetHelp } from './pages/PasswordResetHelp';
import { FirstTimeLoginPage } from './pages/FirstTimeLoginPage';

// Auth debug (lazy load - rarely used)
const AuthDebugPage = lazy(() => import('./pages/AuthDebugPage'));
const AuthDiagnostic = lazy(() => import('./pages/AuthDiagnostic').then(m => ({ default: m.AuthDiagnostic })));

// Home portal (eager load for instant home page)
import EnhancedPortalWithAI from './components/portal/EnhancedPortalWithAI';

// Lazy load all other pages
const OverviewPage = lazy(() => import('./pages/OverviewPage'));
const OverviewAIPage = lazy(() => import('./pages/OverviewPage_NEW'));

// Core module pages (P0 - most frequently accessed)
const LifePage = lazy(() => import('./pages/LifePage').then(m => ({ default: m.LifePage })));
const MoneyPage = lazy(() => import('./pages/MoneyPage'));
const JoyPage = lazy(() => import('./pages/JoyPage'));

// Community pages (P0 - frequently accessed)
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventsDebugPage = lazy(() => import('./pages/EventsDebugPage'));
const EventCreatorPage = lazy(() => import('./pages/EventCreatorPage'));

// Settings and admin (P1 - less frequent)
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const PlaybooksPage = lazy(() => import('./pages/PlaybooksPage'));
const HoverLabsPage = lazy(() => import('./pages/HoverLabsPage'));
const PlaybookCreatorPage = lazy(() => import('./pages/PlaybookCreatorPage'));
const RequestCreatorAccessPage = lazy(() => import('./pages/RequestCreatorAccessPage').then(m => ({ default: m.RequestCreatorAccessPage })));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const RoleDebugPage = lazy(() => import('./pages/RoleDebugPage'));

// AI Co-Navigator (P1 - optional feature)
const AICoNavigatorPage = lazy(() => import('./pages/AICoNavigatorPage').then(m => ({ default: m.AICoNavigatorPage })));
const EventsAdmin = lazy(() => import('./pages/admin/EventsAdmin'));

// Profile pages
const JenProfileSimple = lazy(() => import('./pages/JenProfileSimple'));

// Public info pages
const TechOverviewPage = lazy(() => import('./pages/TechOverviewPage'));
const MarketOverviewPage = lazy(() => import('./pages/MarketOverviewPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const WorkshopPage = lazy(() => import('./pages/WorkshopPage'));
const InteractiveDemoPage = lazy(() => import('./pages/InteractiveDemoPage'));

// Musical event pages
const MusicalPage = lazy(() => import('./pages/MusicalPage').then(m => ({ default: m.MusicalPage })));
const MusicalTestPage = lazy(() => import('./pages/MusicalTestPage').then(m => ({ default: m.MusicalTestPage })));

// Submodule components
const LifeStoriesComponent = lazy(() => import('./components/life/LifeStoriesComponent').then(m => ({ default: m.LifeStoriesComponent })));
const MoneyDecisionsComponent = lazy(() => import('./components/money/MoneyDecisionsComponent').then(m => ({ default: m.MoneyDecisionsComponent })));

// Portal variants (P2 - experimental)
const PortalUnlockParallax = lazy(() => import('./components/portal/PortalUnlockParallax'));
const ParallaxPortal8Layer = lazy(() => import('./components/portal/ParallaxPortal8Layer'));
const UltimatePortalExperience = lazy(() => import('./components/portal/UltimatePortalExperience'));

// Legacy/deprecated pages (P2 - keep for backward compatibility)
const StoryJourneyPage = lazy(() => import('./pages/StoryJourneyPage').then(m => ({ default: m.StoryJourneyPage })));
const ScenarioAnalysisPage = lazy(() => import('./pages/ScenarioAnalysisPage'));
const MoneyCareExamplePage = lazy(() => import('./pages/MoneyCareExamplePage').then(m => ({ default: m.MoneyCareExamplePage })));
const AlerislifePage = lazy(() => import('./pages/AlerislifePage').then(m => ({ default: m.AlerislifePage })));
const EnhancedDecisionAnalysis = lazy(() => import('./components/scenarios/EnhancedDecisionAnalysis'));
const AdvancedDecisionAnalysis = lazy(() => import('./components/scenarios/AdvancedDecisionAnalysis'));

// Test pages (P3 - development only)
const TestPage = lazy(() => import('./pages/TestPage'));
const TestSupabase = lazy(() => import('./pages/TestSupabase'));
const TestCommunityQuery = lazy(() => import('./pages/TestCommunityQuery'));
const MoneyTestPage = lazy(() => import('./pages/MoneyTestPage'));
const SimpleScenarioTest = lazy(() => import('./pages/SimpleScenarioTest'));
const MediaInputDemo = lazy(() => import('./pages/MediaInputDemo').then(m => ({ default: m.MediaInputDemo })));
const UsageTrackingTestPage = lazy(() => import('./pages/UsageTrackingTestPage').then(m => ({ default: m.default })));

// 3D/Interactive pages (P2 - optional visualizations)
const FlightSimulatorPage = lazy(() => import('./pages/FlightSimulatorPage').then(m => ({ default: m.FlightSimulatorPage })));

// 3D/Interactive test pages (P3 - heavy, rarely used)
const FlightSimulatorTest = lazy(() => import('./pages/FlightSimulatorTest').then(m => ({ default: m.FlightSimulatorTest })));
const FlightSimulatorIntegrated = lazy(() => import('./components/flight/FlightSimulatorIntegrated').then(m => ({ default: m.FlightSimulatorIntegrated })));
const Helicopter3DTest = lazy(() => import('./pages/Helicopter3DTest'));
const NatureNavigatorTest = lazy(() => import('./pages/NatureNavigatorTest'));

const queryClient = new QueryClient();

function AppContent() {
  const { isAuthenticated, user } = useSupabaseAuth();
  const { needsOnboarding, isLoading: onboardingLoading, focusArea } = useFirstTimeUser();
  const [location, setLocation] = useLocation();

  // Handle subdomain routing
  useEffect(() => {
    const subdomain = getSubdomain();
    if (subdomain === 'jen' && window.location.pathname === '/') {
      window.location.pathname = '/jen';
    }
  }, []);

  // Handle onboarding completion - redirect to focus area
  const handleOnboardingComplete = () => {
    const savedFocusArea = localStorage.getItem('onboarding_focus') || 'balanced';

    if (savedFocusArea === 'LIFE') {
      setLocation('/life');
    } else if (savedFocusArea === 'MONEY') {
      setLocation('/money');
    } else if (savedFocusArea === 'JOY') {
      setLocation('/joy');
    } else {
      setLocation('/'); // Balanced approach - start at portal
    }
  };

  // Show onboarding for first-time users
  if (isAuthenticated && !onboardingLoading && needsOnboarding) {
    return (
      <ProductionOnboarding onComplete={handleOnboardingComplete} />
    );
  }

  // Public routes (no auth required)
  const publicRoutes = (
    <>
      <Navigation />
      {location !== '/' && <HomeNavigationLogo />}
      {/* Welcome back message for Beta users */}
      {isAuthenticated && location !== '/' && (
        <BetaWelcomeBack />
      )}
      <Suspense fallback={<LoadingFallback />}>
      <Switch>
      {/* Public Routes - No Auth Required */}
      <Route path="/login">
        <SmartLogin />
      </Route>
      <Route path="/login-old" component={LoginComponent} />
      <Route path="/first-time-login" component={FirstTimeLoginPage} />
      <Route path="/auth/callback" component={AuthCallback} />
      <Route path="/auth/verify" component={EmailVerification} />
      <Route path="/auth/reset-password" component={ResetPasswordPage} />
      <Route path="/auth/debug" component={AuthDebugPage} />
      <Route path="/auth/diagnostic" component={AuthDiagnostic} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/password-reset-help" component={PasswordResetHelp} />
      <Route path="/overview" component={OverviewPage} />
      <Route path="/overview-ai" component={OverviewAIPage} />
      <Route path="/demo" component={InteractiveDemoPage} />

      {/* Hidden Public Routes - Not in Navigation */}
      <Route path="/test-supabase" component={TestSupabase} />
      <Route path="/test/money">
        <ProtectedRoute><MoneyTestPage /></ProtectedRoute>
      </Route>
      <Route path="/test-community" component={TestCommunityQuery} />
      <Route path="/tech-overview" component={TechOverviewPage} />
      <Route path="/market-overview" component={MarketOverviewPage} />
      <Route path="/faq" component={FAQPage} />
      <Route path="/workshop" component={WorkshopPage} />
      
      {/* Protected Routes - Auth Required */}
      <Route path="/test">
        <ProtectedRoute><TestPage /></ProtectedRoute>
      </Route>
      <Route path="/test/usage-tracking">
        <ProtectedRoute><UsageTrackingTestPage /></ProtectedRoute>
      </Route>
      <Route path="/flight-simulator-test">
        <ProtectedRoute><FlightSimulatorTest /></ProtectedRoute>
      </Route>
      <Route path="/flight">
        <ProtectedRoute><FlightSimulatorIntegrated /></ProtectedRoute>
      </Route>
      <Route path="/flight-simulator">
        <ProtectedRoute><FlightSimulatorPage /></ProtectedRoute>
      </Route>
      <Route path="/helicopter-3d">
        <ProtectedRoute><Helicopter3DTest /></ProtectedRoute>
      </Route>
      <Route path="/nature">
        <ProtectedRoute><NatureNavigatorTest /></ProtectedRoute>
      </Route>
      <Route path="/nature-test">
        <ProtectedRoute><NatureNavigatorTest /></ProtectedRoute>
      </Route>
      <Route path="/portal-original">
        <ProtectedRoute><PortalUnlockParallax /></ProtectedRoute>
      </Route>
      <Route path="/portal">
        <ProtectedRoute><EnhancedPortalWithAI /></ProtectedRoute>
      </Route>
      <Route path="/portal-8layer">
        <ProtectedRoute><ParallaxPortal8Layer /></ProtectedRoute>
      </Route>
      <Route path="/portal-ultimate">
        <ProtectedRoute><UltimatePortalExperience /></ProtectedRoute>
      </Route>
      <Route path="/demo/media-input">
        <ProtectedRoute><MediaInputDemo /></ProtectedRoute>
      </Route>
      <Route path="/story-journey">
        <ProtectedRoute><StoryJourneyPage /></ProtectedRoute>
      </Route>
      <Route path="/scenario-analysis">
        <ProtectedRoute><ScenarioAnalysisPage /></ProtectedRoute>
      </Route>
      <Route path="/test-scenario">
        <ProtectedRoute><SimpleScenarioTest /></ProtectedRoute>
      </Route>
      <Route path="/enhanced-decision">
        <ProtectedRoute><EnhancedDecisionAnalysis /></ProtectedRoute>
      </Route>
      <Route path="/advanced-decision">
        <ProtectedRoute><AdvancedDecisionAnalysis /></ProtectedRoute>
      </Route>
      <Route path="/alerislife">
        <ProtectedRoute><AlerislifePage /></ProtectedRoute>
      </Route>
      
      {/* LIFE Module Routes */}
      <Route path="/life">
        <ProtectedRoute><LifePage /></ProtectedRoute>
      </Route>
      <Route path="/life/stories">
        <ProtectedRoute><LifeStoriesComponent /></ProtectedRoute>
      </Route>
      <Route path="/life/story-capture">
        <ProtectedRoute><StoryJourneyPage /></ProtectedRoute>
      </Route>
      <Route path="/life/values">
        <ProtectedRoute><StoryJourneyPage /></ProtectedRoute>
      </Route>
      <Route path="/life/mentors">
        <ProtectedRoute><StoryJourneyPage /></ProtectedRoute>
      </Route>
      
      {/* MONEY Module Routes */}
      <Route path="/money">
        <ProtectedRoute><MoneyPage /></ProtectedRoute>
      </Route>
      <Route path="/money/decisions">
        <ProtectedRoute><MoneyDecisionsComponent /></ProtectedRoute>
      </Route>
      <Route path="/money/care-example">
        <ProtectedRoute><MoneyCareExamplePage /></ProtectedRoute>
      </Route>

      {/* JOY Module Routes */}
      <Route path="/joy">
        <ProtectedRoute><JoyPage /></ProtectedRoute>
      </Route>

      {/* Personal Profile - jen.redheli.com */}
      <Route path="/jen">
        <JenProfileSimple />
      </Route>
      
      {/* December 4 Musical */}
      <Route path="/musical">
        <ProtectedRoute><MusicalPage /></ProtectedRoute>
      </Route>
      <Route path="/musical-test">
        <ProtectedRoute><MusicalTestPage /></ProtectedRoute>
      </Route>
      
      {/* Community Hub */}
      <Route path="/community">
        <ProtectedRoute><CommunityPage /></ProtectedRoute>
      </Route>
      
      {/* Events Hub - Public page, no auth required */}
      <Route path="/events">
        <EventsPage />
      </Route>

      {/* Events Debug Page - Diagnostic tool */}
      <Route path="/events-debug">
        <EventsDebugPage />
      </Route>
      <Route path="/bay-nov2025">
        <ProtectedRoute><EventsPage /></ProtectedRoute>
      </Route>

      {/* Event Creator - Creator/Admin only */}
      <Route path="/events/create">
        <ProtectedPlaybooksRoute><EventCreatorPage /></ProtectedPlaybooksRoute>
      </Route>

      {/* Production Playbooks - Creator/Admin only */}
      <Route path="/playbooks/create">
        <ProtectedPlaybooksRoute><PlaybookCreatorPage /></ProtectedPlaybooksRoute>
      </Route>
      <Route path="/playbooks/:playbookId">
        <ProtectedPlaybooksRoute><PlaybooksPage /></ProtectedPlaybooksRoute>
      </Route>
      <Route path="/playbooks">
        <ProtectedPlaybooksRoute><PlaybooksPage /></ProtectedPlaybooksRoute>
      </Route>

      {/* Hover Labs - Strategic Framework (Creator/Admin only) */}
      <Route path="/labs">
        <ProtectedPlaybooksRoute><HoverLabsPage /></ProtectedPlaybooksRoute>
      </Route>

      {/* Request Creator Access - Protected (must be logged in) */}
      <Route path="/request-creator-access">
        <ProtectedRoute><RequestCreatorAccessPage /></ProtectedRoute>
      </Route>

      {/* Settings & Data Backup */}
      <Route path="/settings">
        <ProtectedRoute><SettingsPage /></ProtectedRoute>
      </Route>

      {/* Privacy Policy - Public access (GDPR requirement) */}
      <Route path="/privacy-policy" component={PrivacyPolicyPage} />

      {/* AI Co-Navigator - Protected, requires AI consent */}
      <Route path="/ai-conavigator">
        <ProtectedRoute><AICoNavigatorPage /></ProtectedRoute>
      </Route>

      {/* Role Debug Page - For checking user roles */}
      <Route path="/role-debug">
        <ProtectedRoute><RoleDebugPage /></ProtectedRoute>
      </Route>

      {/* Admin - Role Debug (accessible to all authenticated users) */}
      <Route path="/admin">
        <ProtectedRoute><RoleDebugPage /></ProtectedRoute>
      </Route>

      {/* Admin Panel - Admin only */}
      <Route path="/admin/panel">
        <ProtectedAdminRoute><AdminPanel /></ProtectedAdminRoute>
      </Route>

      {/* Admin Events Editor - Admin only */}
      <Route path="/admin/events">
        <ProtectedAdminRoute><EventsAdmin /></ProtectedAdminRoute>
      </Route>

      {/* Home Page - Public */}
      <Route path="/">
        <RecoveryRedirect>
          <EnhancedPortalWithAI />
        </RecoveryRedirect>
      </Route>
    </Switch>
    </Suspense>
    </>
  );

  return publicRoutes;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <LanguageProvider>
            <SupabaseAuthProvider>
              <AIConsentProvider>
                <AppContent />
              </AIConsentProvider>
            </SupabaseAuthProvider>
          </LanguageProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;